import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAssetImportBatch,
  useAssetImportProvenance,
  useUndoAssetImport,
} from 'hooks/assetImports/assetImportHooks';
import {
  formatProvenanceLabel,
  importSourceLabel,
} from 'hooks/assetImports/assetImportLabels';

/** Confirm, then archive every asset a PRATUS AI import created. */
export function UndoAssetImportButton({
  batchId,
  sourceLabel,
  createdCount,
  onUndone,
}: {
  batchId: string;
  sourceLabel: string;
  createdCount?: number;
  onUndone?: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const undo = useUndoAssetImport();
  const assets =
    createdCount === undefined
      ? 'every asset'
      : `the ${createdCount} asset${createdCount === 1 ? '' : 's'}`;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={undo.isPending}
        onClick={() => setConfirming(true)}
      >
        Undo this import
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this import?</AlertDialogTitle>
            <AlertDialogDescription>
              This archives {assets} created from {sourceLabel}. Assets the
              import only updated are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep assets</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setConfirming(false);
                undo.mutate(batchId, {
                  onSuccess: (result) => {
                    toast.success(
                      `Archived ${result.archived} imported asset${result.archived === 1 ? '' : 's'}.`
                    );
                    onUndone?.();
                  },
                  onError: (error) => {
                    const message =
                      (error as { response?: { data?: { message?: string } } })
                        .response?.data?.message ??
                      'Could not undo the import.';
                    toast.error(message);
                  },
                });
              }}
            >
              Undo import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** "Imported by PRATUS AI from …" line on an asset, with undo for its import. */
export function AssetImportProvenance({
  resourceSetId,
  onUndone,
}: {
  resourceSetId: string | undefined;
  onUndone?: () => void;
}) {
  const { data } = useAssetImportProvenance(
    resourceSetId ? [resourceSetId] : []
  );
  const provenance = data?.find((p) => p.resource_set_id === resourceSetId);
  const canUndo = provenance?.batch_status === 'committed';
  const { data: batch } = useAssetImportBatch(
    canUndo ? provenance?.batch_id : undefined
  );
  if (!provenance) return null;

  return (
    <div
      className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-border tw:px-3 tw:py-2"
      data-testid="asset-import-provenance"
    >
      <p className="tw:m-0 tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground">
        <Sparkles className="tw:size-3.5" aria-hidden />
        {formatProvenanceLabel(provenance)}
      </p>
      {canUndo && (
        <UndoAssetImportButton
          batchId={provenance.batch_id}
          sourceLabel={importSourceLabel(provenance)}
          createdCount={batch?.created}
          onUndone={onUndone}
        />
      )}
    </div>
  );
}
